import { LoadingScreen } from "@flat-ui";
import { QueryParams } from "@router/route-params";
import { runtimeConfig } from "@src/runtime-config";
import { isOAuthProvider } from "@utils/oauth-provider-utils";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProviderLoginSelector } from "@pages/provider-login/provider-login-selector";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { SerializedError } from "@reduxjs/toolkit";
import { isValidUlmClient } from "@utils/ulm-utils";
import { REGION_SERVER_IDENTIFIER } from "@constants/server-constants";
import { generateLoginRoute } from "@router/router-helper";

/** Server identifier for the current region */
const regionServerIdentifier =
  REGION_SERVER_IDENTIFIER[runtimeConfig.appRegion];

/** Redirects to the authentication provider login page */
export function ProviderLogin(): JSX.Element {
  const [searchParams] = useSearchParams();
  const email = searchParams.get(QueryParams.email);
  const redirectUrlParam = searchParams.get(QueryParams.redirectUrl);
  const redirectUrl = redirectUrlParam || runtimeConfig.urls.sphereDashboardUrl;
  const providerParam = searchParams.get(QueryParams.provider);
  const clientParam = searchParams.get(QueryParams.client);
  const client = isValidUlmClient(clientParam) ? clientParam : null;
  const { handleError } = useErrorHandlerContext();
  const navigate = useNavigate();

  const isValidProvider =
    providerParam !== null &&
    isOAuthProvider(providerParam) &&
    runtimeConfig.oauth.providers.includes(providerParam);

  const validProvider = isValidProvider ? providerParam : null;

  const [provider, setProvider] =
    useState<FaroCoreApiTypes.OAuthProvider | null>(validProvider);

  // Log error with toast if the provider param is passed but is invalid
  useEffect(() => {
    if (!isValidProvider && providerParam !== null) {
      const rawError = new Error(
        `The log in provider "${providerParam}" you are trying to use is not supported. Please contact ${runtimeConfig.mailSupportAddress} if you believe this is an error.`
      );
      const serializedError: SerializedError = {
        name: rawError.name,
        message: rawError.message,
        stack: rawError.stack,
      };

      handleError({
        id: `invalid-oauth-provider-param-${Date.now().toString()}`,
        title: "Invalid OAuth provider.",
        error: serializedError,
        uiType: "toast",
      });
    }
  }, [isValidProvider, providerParam, handleError]);

  // Redirect to the authentication provider login page
  useEffect(() => {
    // Special case for the HB JobWalk app
    // If the selected SSO provider is "faro-sphere" then redirect to the login page
    // So that the user can input their email address and carry their login flow from there.
    if (client === "jwahb" && provider === "faro-sphere") {
      const params = new URLSearchParams(window.location.search);
      // Set the server so that user is redirected to the correct server login page
      params.set(QueryParams.server, regionServerIdentifier);
      // Hide the "Get started" text and link from the ULM login page since this is a SSO login flow
      params.set(QueryParams.hideRegistration, "true");
      void navigate({
        pathname: generateLoginRoute(),
        search: params.toString(),
      });
      return;
    }

    if (provider) {
      const url = new URL("oauth", runtimeConfig.urls.apiBaseUrl);
      url.searchParams.set(QueryParams.provider, provider);
      url.searchParams.set(QueryParams.redirectUrl, redirectUrl);

      if (email) {
        url.searchParams.set(QueryParams.email, email);
      }

      window.location.href = url.href;
    }
  }, [provider, redirectUrl, email, client, navigate]);

  return (
    <>
      {!provider && <ProviderLoginSelector setProvider={setProvider} />}
      {provider && <LoadingScreen />}
    </>
  );
}
